<template>
  <div class="row">
    <model-index
      ref="table"
      class="col-12"
      :loading="loading"
      v-bind="$props"
      :columns="columns"
      :busy.sync="busy"
      :models-paginated="modelsPaginated"
      :models="models"
      :page.sync="page"
      :per-page.sync="perPage"
      :from="from"
      :total="totalFiltered"
      :search.sync="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :empty-state-title="$t('trips.empty_state.title')"
      empty-state-subtitle=" "
      @create-click="handleCreate"
      @show-click="handleShow"
      @edit-click="handleEdit"
      @delete-click="handleDelete"
    >
      <template #emptyStateAction>
        <div v-if="canCreate" class="d-flex justify-content-center">
          <b-button variant="primary" @click="handleCreate">
            {{ $t('trips.empty_state.action') }}
          </b-button>
        </div>
      </template>
    </model-index>
  </div>
</template>

<script>
import ModelChildIndex from "../../models/children/index";
import {hasAllowedAction, normalizeAllowedActions} from "@/helpers/ui/allowedActions";

export default {
  extends: ModelChildIndex,
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("trips.plural"));
      }
    },
    resourceRoute: { default: "trips" },
    modelVariable: { default: "trip" },
    tableMinHeight: { type: Number, default: 85.5 }, // 42.5 + 43
    tableHeight: { type: Number, default: 214.5 }, // 42.5 + (43 * 4)
    denyCreate: { type: Boolean, default: true },
    createRoute: { type: [String, Object], default: null },
    hideTableHeader: { type: Boolean, default: true },
    values: {
      type: Array,
      default() {
        return [];
      }
    },
    showProps: {
      default() {
        return {
          cardTitle: this.lodash.startCase(this.$t("trips.singular")),
          resourceRoute: "trips",
          modelVariable: "trip",
          modelMapped: require("@/router/views/trips/show.vue").default.computed.modelMapped.bind(this),
          denyEdit: true,
          denyDelete: true
        };
      }
    }
  },
  data() {
    return {
      sortBy: "tripped_at",
      sortDesc: true,
      allowedActions: []
    };
  },
  computed: {
    canCreate() {
      return hasAllowedAction(this.allowedActions, ["create", "store"]);
    },
    columns() {
      return {
        ["trip"]: [
          {
            key: "id",
            label: this.lodash.startCase(this.$t("tickets.trip_id")),
            thStyle: { minWidth: "125px" },
            component: {
              props: ["item"],
              template: '<router-link :to="`/trips/${item.id}`">{{ `${item.id }` }}</router-link>'
            }
          },
          {
            key: "trip_items.purchase_item.purchase_order_id",
            label: this.lodash.startCase(this.$t(`purchase_payments.purchase_order_id`)),
            thStyle: { minWidth: "100px" },
            component: {
              props: ["item"],
              template:
                '<div><template v-for="(purchaseOrder, index) in item.purchase_orders"><router-link :to="`/purchase_orders/${purchaseOrder.id}`">{{ `${purchaseOrder.id}` }}</router-link><span v-if="index !== (item.purchase_orders || []).length - 1">, </span></template></div>'
            }
          },
          {
            key: "tripped_at",
            label: this.lodash.startCase(this.$t("trips.tripped_at")),
            thStyle: { minWidth: "125px" },
            component: {
              props: ["item"],
              template:
                '<div v-if="item.tripped_at" style="width: 125px" class="text-truncate" :title="item.tripped_at">{{ $moment.utc(item.tripped_at).local().format(`DD MMM YYYY`) }}</div>' +
                "<div v-else>-</div>"
            }
          },
          {
            key: "trip_items.purchase_item.purchase_order.customer.name",
            label: this.lodash.startCase(this.$t(`trips.partner`)),
            thStyle: { minWidth: "100px" },
            component: {
              props: ["item"],
              template:
                '<div><template v-for="(purchaseOrder, index) in item.purchase_orders"><router-link :to="`/customers/${purchaseOrder.customer.id}`">{{ `${purchaseOrder.customer.name}` }}</router-link><span v-if="index !== (item.purchase_orders || []).length - 1">, </span></template></div>'
            }
          },
          {
            key: "customer.name",
            label: this.lodash.startCase(this.$t("trips.factory_destination")),
            component: {
              props: ["item"],
              methods: {
                format({ type, customer }) {
                  if (type === "hybrid") return "-";
                  return customer.name ?? "-";
                }
              },
              template: "<span>{{format(item)}}</span>"
            }
          }
        ]
      };
    }
  },
  methods: {
    handleCreate() {
      if (!this.canCreate) {
        return;
      }

      if (this.createRoute) {
        return this.$router.push(this.createRoute);
      }

      return ModelChildIndex.methods.handleCreate.call(this);
    },
    store(value) {
      return this.$axios.post(`/trips`, value);
    },
    update(value) {
      return this.$axios.put(`/trips/${this.getModelKey(value)}`, value);
    },
    delete(value) {
      return this.$axios.delete(`/trips/${this.getModelKey(value)}`);
    },
    fetchModels(page = 1) {
      this.allowedActions = [];

      return this.$axios
        .get(`/trips`, {
          params: {
            page: page,
            per_page: this.perPage,
            search: this.search,
            sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
            with_sub_product: "1",
            with: [
              "customer",
              "trip_items.purchase_order.purchase_items",
              "trip_items.purchase_order.ticket",
              "trip_items.purchase_order.customer",
              "vehicle",
              "driver",
              "creator",
              "sale_order"
            ].join(","),
            sales_plan_id: this.parent.id || this.$route.params.key,
            ...this.filterParams
          }
        })
        .then(response => {
          const {
            data: { data, meta }
          } = response;
          this.allowedActions = normalizeAllowedActions(meta?.allowed_actions);
          this.models = data.map(function(datum) {
            let purchaseOrders = [];
            datum.trip_items.forEach(tripItem => {
              let purchaseOrderIds = purchaseOrders.map(purchaseOrder => purchaseOrder.id);
              if (!purchaseOrderIds.includes(tripItem.purchase_order.id)) {
                purchaseOrders.push(tripItem.purchase_order);
              }
            });

            return {
              ...datum,
              ticket: datum.trip_items.length ? datum.trip_items[0].purchase_order.ticket : null,
              purchase_orders: purchaseOrders
            };
          });
          this.page = Number(meta.current_page);
          this.perPage = Number(meta.per_page);
          this.from = Number(meta.from);
          this.total = Number(meta.total);

          this.stats = [];

          return response;
        })
        .catch(error => {
          this.allowedActions = [];
          this.failedAlert(error);
          return null;
        });
    }
  }
};
</script>
